// ABOUTME: Keeps the discussion's counters from going back: an answer that brings an item's counters older than the
// ABOUTME: cached ones — a calendar read sent before a read mark, answered after it — carries the cached ones instead.
import { ApolloLink } from "@apollo/client"
import { Observable } from "@apollo/client/utilities"
import { Kind, type DocumentNode } from "graphql"
import { keepNewerSummaries } from "@/model/discussion/discussionCache"

/** Every read of the counters goes through this fragment (discussion.graphqls); nothing else needs looking at. */
const selectsSummary = (query: DocumentNode): boolean =>
  query.definitions.some(
    (definition) => definition.kind === Kind.FRAGMENT_DEFINITION && definition.name.value === "DiscussionSummary",
  )

/**
 * The counters are a snapshot, and of two the one behind on the read mark or the newest number is the
 * older (newerSummary). Apollo writes an answer into the normalized counters as it comes — the
 * incoming value wins (entityStore.js:655-663) — and no cache rule can stop that for a normalized
 * entity: a type's merge reaches it only when one of its fields has a merge of its own
 * (writeToStore.js:75-113). The answer itself is the one place every network write passes: calendar
 * reads, mutations, the feed's pages. An answer read `no-cache` and written later by hand — a primed
 * week, the coach's batch — is checked again right before that write (primeQueries).
 */
export function createSummaryFreshnessLink(): ApolloLink {
  return new ApolloLink((operation, forward) => {
    if (!selectsSummary(operation.query)) return forward(operation)
    return new Observable((observer) => {
      const subscription = forward(operation).subscribe({
        next: (result) =>
          observer.next(
            result.data
              ? { ...result, data: keepNewerSummaries(operation.client.cache, result.data) as typeof result.data }
              : result,
          ),
        error: (error: unknown) => observer.error(error),
        complete: () => observer.complete(),
      })
      return () => subscription.unsubscribe()
    })
  })
}
